import { buildCollection, buildProperty, EntityCustomView } from "@firecms/core";
import { HomeButton } from "../components/HomeButton";
import { CampaignActionsView } from "../components/CampaignActionsView";

export type Campaign = {
    name: string;
    description?: string;
    text_credits: number;
    voice_credits: number;
    target_type: string;
    target_tiers?: string[];
    target_last_seen_after?: Date;
    target_last_seen_before?: Date;
    target_user_ids?: string[];
    status: string;
    scheduled_for?: Date;
    users_targeted?: number;
    users_granted?: number;
    error_message?: string;
    created_at: Date;
    created_by: string;
    executed_at?: Date;
};

const campaignViews: EntityCustomView<Campaign>[] = [
    {
        key: "actions",
        name: "Execute",
        Builder: CampaignActionsView,
    },
];

export const campaignsCollection = buildCollection<Campaign>({
    id: "campaigns",
    name: "Campaigns",
    singularName: "Campaign",
    path: "campaigns",
    icon: "Campaign",
    group: "Promotions",
    Actions: HomeButton,
    description: "Bulk bonus grant campaigns",
    entityViews: campaignViews,
    permissions: ({ entity }) => {
        // Only allow creating/editing if it's a draft
        const isDraft = !entity || entity.values.status === "draft" || entity.values.status === "scheduled";
        return {
            edit: isDraft,
            create: true,
            delete: isDraft,
        };
    },
    properties: {
        name: buildProperty({
            name: "Campaign Name",
            dataType: "string",
            validation: { required: true },
        }),
        description: buildProperty({
            name: "Description",
            dataType: "string",
            multiline: true,
        }),
        text_credits: buildProperty({
            name: "Text Credits to Grant",
            dataType: "number",
            validation: { required: true, min: 0 },
            defaultValue: 0,
        }),
        voice_credits: buildProperty({
            name: "Voice Credits to Grant",
            dataType: "number",
            validation: { required: true, min: 0 },
            defaultValue: 0,
        }),
        target_type: buildProperty({
            name: "Target Type",
            dataType: "string",
            enumValues: {
                tier: "By Subscription Tier",
                activity: "By Activity (Last Seen)",
                manual: "Manual Selection",
            },
            validation: { required: true },
        }),
        target_tiers: buildProperty({
            name: "Target Tiers",
            dataType: "array",
            of: {
                dataType: "string",
                enumValues: {
                    free: "Free",
                    pro: "Pro",
                },
            },
            description: "Select tiers to target (for 'By Subscription Tier')",
        }),
        target_last_seen_after: buildProperty({
            name: "Last Seen After",
            dataType: "date",
            mode: "date",
            description: "Users who were active after this date",
        }),
        target_last_seen_before: buildProperty({
            name: "Last Seen Before",
            dataType: "date",
            mode: "date",
            description: "Users who were active before this date (for lapsed users)",
        }),
        target_user_ids: buildProperty({
            name: "User IDs (Manual)",
            dataType: "array",
            of: { dataType: "string" },
            description: "Comma-separated Telegram user IDs",
        }),
        status: buildProperty({
            name: "Status",
            dataType: "string",
            enumValues: {
                draft: "📝 Draft",
                scheduled: "📅 Scheduled",
                executing: "⏳ Executing",
                completed: "✅ Completed",
                failed: "❌ Failed",
            },
            defaultValue: "draft",
        }),
        scheduled_for: buildProperty({
            name: "Scheduled For",
            dataType: "date",
            mode: "date_time",
            description: "When to execute (for scheduled campaigns)",
        }),
        users_targeted: buildProperty({
            name: "Users Targeted",
            dataType: "number",
            readOnly: true,
        }),
        users_granted: buildProperty({
            name: "Users Granted",
            dataType: "number",
            readOnly: true,
        }),
        error_message: buildProperty({
            name: "Error Message",
            dataType: "string",
            readOnly: true,
        }),
        created_at: buildProperty({
            name: "Created At",
            dataType: "date",
            mode: "date_time",
            autoValue: "on_create",
            readOnly: true,
        }),
        created_by: buildProperty({
            name: "Created By",
            dataType: "string",
            readOnly: true,
        }),
        executed_at: buildProperty({
            name: "Executed At",
            dataType: "date",
            mode: "date_time",
            readOnly: true,
        }),
    },
    propertiesOrder: [
        "name",
        "description",
        "text_credits",
        "voice_credits",
        "target_type",
        "target_tiers",
        "target_last_seen_after",
        "target_last_seen_before",
        "target_user_ids",
        "status",
        "scheduled_for",
        "users_targeted",
        "users_granted",
    ],
});
